import { Button } from './Button';
import { Select } from './Select';
import { useTranslation } from '@/i18n';
import styles from './Pagination.module.css';

interface PaginationProps {
  /** The page the SERVER returned, which is not always the one asked for. */
  page: number;
  rows: number;
  total: number;
  /**
   * What is being counted — "Flights", "Airports".
   *
   * Passed in rather than fixed here: this is the second table to use the pager, and
   * a shared component that says "Flights" above a list of airports is worse than
   * one that asks its caller what it is showing.
   */
  countLabel: string;
  /**
   * The total saturated at its cap and means "at least this many". The count renders
   * with a `+`, and the page count — which would be a fiction — is not shown and the
   * last-page button not offered. `isLastPage` stays authoritative throughout.
   */
  capped?: boolean;
  /** The server's own answer, rather than a page count computed from the total. */
  isLastPage: boolean;
  rowsOptions: readonly number[];
  onPage: (page: number) => void;
  onRows: (rows: number) => void;
  isLoading?: boolean;
}

/**
 * The pager.
 *
 * "Next" is disabled by `lastBlock` from the response rather than by comparing the
 * page to `ceil(total / rows)`: asking this API for a page past the last returns the
 * LAST page, so probing for an empty one just silently re-renders what is already on
 * screen.
 */
export const Pagination = ({
  page,
  rows,
  total,
  countLabel,
  capped = false,
  isLastPage,
  rowsOptions,
  onPage,
  onRows,
  isLoading = false,
}: PaginationProps) => {
  const { t, formatNumber } = useTranslation();

  const pageCount = Math.max(1, Math.ceil(total / rows));

  return (
    <div className={styles.bar}>
      <span className={styles.count}>
        {countLabel}:{' '}
        <span className={styles.countValue}>
          {formatNumber(total)}
          {capped && '+'}
        </span>
      </span>

      <label className={styles.size}>
        {t('schedule.perPage')}
        <Select
          options={rowsOptions.map((option) => ({ value: String(option), label: String(option) }))}
          value={String(rows)}
          onChange={(event) => onRows(Number(event.target.value))}
        />
      </label>

      <div className={styles.controls}>
        <Button
          size="sm"
          onClick={() => onPage(1)}
          disabled={page <= 1 || isLoading}
          aria-label={t('schedule.firstPage')}
          title={t('schedule.firstPage')}
        >
          ‹‹
        </Button>
        <Button
          size="sm"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1 || isLoading}
          aria-label={t('schedule.previousPage')}
          title={t('schedule.previousPage')}
        >
          ‹
        </Button>

        <span className={styles.page} aria-live="polite">
          {capped ? t('schedule.pageOnly', { page }) : t('schedule.pageOf', { page, pages: pageCount })}
        </span>

        <Button
          size="sm"
          onClick={() => onPage(page + 1)}
          disabled={isLastPage || isLoading}
          aria-label={t('schedule.nextPage')}
          title={t('schedule.nextPage')}
        >
          ›
        </Button>
        {/* Only when the total is exact: with a capped count the page count is a
            fiction, and a button jumping to a page that may not exist is worse than
            no button. */}
        {!capped && (
          <Button
            size="sm"
            onClick={() => onPage(pageCount)}
            disabled={isLastPage || isLoading}
            aria-label={t('schedule.lastPage')}
            title={t('schedule.lastPage')}
          >
            ››
          </Button>
        )}
      </div>
    </div>
  );
};
